import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  CheckBox,
  Tabs,
  TopProgress,
} from "../../../../../components";
import { permissionsService } from "../../../../../api";
import PermissionsSelector from "../../../../settings/authorization/components/PermissionsSelector";
import {
  ActionsRow,
  Card,
  EmptyState,
  InfoBox,
  InfoText,
  PermissionsWrapper,
  SectionTitle,
  TabsWrapper,
} from "../../../../settings/authorization/styles";
import {
  flattenPermissions,
  PermissionsByCategory,
  RolePermissions,
} from "../../../../settings/authorization/types";
import { useUi } from "../../../../../context/ui.context";

interface PermissionsProps {
  roleId: string;
  role?: any;
}

const Permissions: FC<PermissionsProps> = ({ roleId, role }) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [rolePermissions, setRolePermissions] =
    useState<RolePermissions | null>(null);
  const [activePanel, setActivePanel] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const loadPermissions = useCallback(async () => {
    if (!roleId) {
      setRolePermissions(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await permissionsService.getRolePermissions(roleId);
      const data = response?.data as RolePermissions;
      setRolePermissions(data);

      const firstPanel = Object.keys(data?.permissions || {})[0];
      setActivePanel(firstPanel || "");
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          t("FAILED_TO_LOAD_DATA"),
      });
    } finally {
      setLoading(false);
    }
  }, [roleId, t, viewAlert]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  useEffect(() => {
    if (!rolePermissions) {
      return;
    }

    const panelKeys = Object.keys(rolePermissions.permissions || {});
    if (panelKeys.length && !panelKeys.includes(activePanel)) {
      setActivePanel(panelKeys[0]);
    }
  }, [activePanel, rolePermissions]);

  const panelTabs = useMemo(() => {
    if (!rolePermissions?.permissions) {
      return [];
    }

    return Object.keys(rolePermissions.permissions).map((panelKey) => ({
      title:
        rolePermissions.panel_display_names?.[panelKey] ||
        panelKey.replace(/_/g, " "),
      slug: panelKey,
      id: panelKey,
    }));
  }, [rolePermissions]);

  const handlePanelPermissionsChange = useCallback(
    (panelKey: string, updated: PermissionsByCategory) => {
      setRolePermissions((prev) => {
        if (!prev) {
          return prev;
        }

        return {
          ...prev,
          permissions: {
            ...prev.permissions,
            [panelKey]: updated,
          },
        };
      });
    },
    []
  );

  const handleSave = useCallback(async () => {
    if (!rolePermissions) {
      return;
    }

    setSaving(true);
    try {
      await permissionsService.saveRolePermissions({
        ...rolePermissions,
        permissions: flattenPermissions(rolePermissions.permissions),
      });
      viewAlert({
        severity: "success",
        message: t("ALL_CHANGES_SAVED_SUCCESSFULLY"),
      });
      await loadPermissions();
    } catch (e: any) {
      viewAlert({
        severity: "error",
        message:
          e?.response?.data?.detail ||
          e?.response?.data?.error ||
          t("ERROR_OCCURRED"),
      });
    } finally {
      setSaving(false);
    }
  }, [loadPermissions, rolePermissions, t, viewAlert]);

  const permissionsForPanel =
    activePanel && rolePermissions?.permissions?.[activePanel];
  const canEditPermissions = role ? !role.default : false;

  return (
    <Card>
      <TopProgress loading={loading || saving} />
      <SectionTitle>{t("PERMISSIONS")}</SectionTitle>

      {rolePermissions && (
        <>
          <InfoBox>
            <InfoText>
              {canEditPermissions
                ? `${t("PERMISSIONS")}: ${role?.name || role?.display_name || `#${roleId}`}`
                : `${t("PERMISSIONS")} ${role?.name || role?.display_name || `#${roleId}`}`}
            </InfoText>
          </InfoBox>

          <CheckBox
            title={t("AUTHORIZATION_GRANT_IMPLICITLY")}
            value={rolePermissions.implicitly_granted}
            disabled={!canEditPermissions}
            onChange={() => {
              if (!canEditPermissions) {
                return;
              }

              setRolePermissions((prev) =>
                prev
                  ? {
                      ...prev,
                      implicitly_granted: !prev.implicitly_granted,
                    }
                  : prev
              );
            }}
          />

          <PermissionsWrapper>
            <TabsWrapper>
              <Tabs
                tabs={panelTabs}
                size="display"
                blockNavigate
                location={activePanel}
                onChange={(panel: string) => setActivePanel(panel)}
              />
            </TabsWrapper>

            {permissionsForPanel ? (
              <PermissionsSelector
                permissions={permissionsForPanel}
                disabled={!canEditPermissions}
                onChange={(updated: PermissionsByCategory) =>
                  handlePanelPermissionsChange(activePanel, updated)
                }
              />
            ) : (
              <EmptyState>{t("AUTHORIZATION_NO_PERMISSIONS_FOUND")}</EmptyState>
            )}
          </PermissionsWrapper>

          <ActionsRow>
            <Button
              size="mid"
              title={t("AUTHORIZATION_SAVE_PERMISSIONS")}
              onClick={handleSave}
              disabled={!canEditPermissions || saving || !rolePermissions}
            />
          </ActionsRow>
        </>
      )}

      {!loading && !rolePermissions && (
        <EmptyState>{t("AUTHORIZATION_NO_PERMISSIONS_FOUND")}</EmptyState>
      )}
    </Card>
  );
};

export default Permissions;
